/**
 * Column definitions for ListView: global (and firm documents) vs case/lead/client.
 * Extracted to keep ListView.jsx under 300 lines.
 */

import React from 'react';
import { Tooltip } from 'primereact/tooltip';

import HeaderCheckbox from './templates/HeaderCheckbox';

import { TitleTemplate } from './templates/TitleTemplate';
import SizeTemplate from './templates/SizeTemplate';
import PagesTemplate from './templates/PagesTemplate';
import CheckboxTemplate from './templates/CheckboxTemplate';
import SignatureTemplate from './templates/SignatureTemplate';
import LastEditedTemplate from './templates/LastEditedTemplate';
import UploadedTemplate from './templates/UploadedTemplate';
import ActionsTemplate from './templates/ActionsTemplate/ActionsTemplate';
import FileSharingDetailsCard from 'components/FileSharing/FileSharingDetailsCard';
import GlobalTooltip from 'components/GlobalTooltip';
import { isCurrentDatePastExpiry } from 'utils/utility_functions/isCurrentDatePastExpiry';

/**
 * @param {Object} params - userContext, isClientFileManager, files, selectedRows, handleSelectAllCheckboxClick, handleSingleCheckboxClick, itemsRef, caseDetails, handleGetFiles, pageLimit, breadCrumbs, setShowFileSharingDetailsModal, setSelectedFile
 */
export function getCaseColumns(params) {
  const {
    userContext,
    isClientFileManager,
    files,
    selectedRows,
    handleSelectAllCheckboxClick,
    handleSingleCheckboxClick,
    itemsRef,
    caseDetails,
    handleGetFiles,
    pageLimit,
    breadCrumbs,
    setShowFileSharingDetailsModal,
    setSelectedFile,
  } = params;

  const handleShowFileSharingDetailsModal = (rowData) => {
    setShowFileSharingDetailsModal(true);
    setSelectedFile(rowData);
  };

  const titleBody = (rowData, event) => {
    return <TitleTemplate rowData={rowData} event={event} isClientFileManager={isClientFileManager} isGlobalFileManager={false} />;
  };

  const sharedBody = (rowData) => {
    const isFolder = rowData?.entry_type === 'folder';

    if (isFolder) {
      const shareStatus = rowData?.contents_share_status;

      if (shareStatus === 'fully_shared') {
        return (
          <>
            <span className="p-column-title text-break">Shared</span>
            <span className="file-sharing-details d-flex align-items-center gap-2">
              <GlobalTooltip tooltip="All items shared with client" position="top">
                <i
                  className="fas fa-folder F-size16 pointer share-details"
                  style={{ color: '#2fa84f' }}
                  onClick={() => handleShowFileSharingDetailsModal(rowData)}
                />
              </GlobalTooltip>
            </span>
          </>
        );
      }

      if (shareStatus === 'partially_shared') {
        return (
          <>
            <span className="p-column-title text-break">Shared</span>
            <span className="file-sharing-details d-flex align-items-center gap-2">
              <GlobalTooltip tooltip="Some items shared with client" position="top">
                <i
                  className="fas fa-folder F-size16 pointer share-details"
                  style={{
                    color: '#dcfce7',
                    WebkitTextStroke: '1.5px #374151',
                    paintOrder: 'stroke fill',
                  }}
                  onClick={() => handleShowFileSharingDetailsModal(rowData)}
                />
              </GlobalTooltip>
            </span>
          </>
        );
      }

      return (
        <>
          <span className="p-column-title text-break">Shared</span>
          <span className="file-sharing-details d-flex align-items-center gap-2">
            <GlobalTooltip tooltip="Nothing in this folder is shared" position="top">
              <i className="fas fa-times-circle F-size16" style={{ cursor: 'default' }} />
            </GlobalTooltip>
          </span>
        </>
      );
    }

    const isSharedViaLink = isCurrentDatePastExpiry(rowData?.lawft_link_expires_on, userContext?.userDetails?.timezone) === false;
    const isSharedWithContact = rowData?.file_shared_contact_id?.length > 0;
    const isFileShared = isSharedViaLink || isSharedWithContact;

    return (
      <>
        <span className="p-column-title text-break">Shared</span>
        <span className="file-sharing-details d-flex align-items-center gap-3">
          {isFileShared ? (
            <>
              <span style={{ display: 'inline-flex', width: '1rem', justifyContent: 'center' }}>
                {isSharedWithContact && (
                  <GlobalTooltip tooltip="Shared with client" position="top">
                    <i className="fas fa-check-circle F-size16 shared-icon" style={{ cursor: 'default' }} />
                  </GlobalTooltip>
                )}
              </span>
              <span style={{ display: 'inline-flex', width: '1rem', justifyContent: 'center' }}>
                {isSharedViaLink && (
                  <>
                    <i
                      id={`link-share-tooltip-${rowData.sk}`}
                      className="fas fa-link F-size16 pointer share-details"
                      style={{ color: '#4f46e5' }}
                      onClick={() => handleShowFileSharingDetailsModal(rowData)}
                    />
                    <Tooltip
                      target={`#link-share-tooltip-${rowData.sk.replace(/[^\w-]/g, '\\$&')}`}
                      className="file-share-tooltip"
                      position="top"
                    >
                      <FileSharingDetailsCard rowData={rowData} />
                    </Tooltip>
                  </>
                )}
              </span>
            </>
          ) : (
            <GlobalTooltip tooltip="Not shared" position="top">
              <i className="fas fa-times-circle F-size16" style={{ cursor: 'default' }} />
            </GlobalTooltip>
          )}
        </span>
      </>
    );
  };

  const actionsBody = (rowData, e) => (
    <div className="text-right d-flex w-100 align-items-center">
      <ActionsTemplate
        rowData={rowData}
        e={e}
        itemsRef={itemsRef}
        caseDetails={caseDetails}
        onDownloadComplete={() => handleGetFiles(pageLimit, breadCrumbs?.[breadCrumbs.length - 1]?.value)}
      />
    </div>
  );

  return [
    {
      header: (
        <HeaderCheckbox
          handleSelectAllCheckboxClick={() => handleSelectAllCheckboxClick(files)}
          selectAll={params.selectAll}
          selectedRows={selectedRows}
          files={files}
        />
      ),
      body: (rowData) => (
        <CheckboxTemplate handleSingleCheckboxClick={handleSingleCheckboxClick} selectedRows={selectedRows} rowData={rowData} />
      ),
      bodyClassName: 'ellipsis-text',
      fixed: true,
      width: '40px',
      field: 'id',
    },
    {
      field: 'display_name',
      header: 'Title',
      body: titleBody,
      sortable: true,
      bodyClassName: 'fileManager-name',
    },
    {
      field: 'size',
      header: 'File Size',
      body: (rowData) => <SizeTemplate rowData={rowData} />,
      sortable: true,
      bodyClassName: 'ellipsis-text',
      width: '100px',
    },
    {
      field: 'pages',
      header: 'Pages',
      body: (rowData) => <PagesTemplate rowData={rowData} />,
      sortable: false,
      bodyClassName: 'ellipsis-text',
      width: '80px',
    },
    {
      field: 'signature',
      header: 'Signature',
      body: (rowData) => <SignatureTemplate rowData={rowData} />,
      bodyClassName: 'ellipsis-text',
      hidden: !userContext.checkSubPermission('e_signature'),
    },
    {
      field: 'time',
      header: 'Date Uploaded',
      body: (rowData) => <UploadedTemplate userContext={userContext} rowData={rowData} isClientFileManager={isClientFileManager} />,
      sortable: true,
    },
    {
      field: 'updated',
      header: 'Last Edited',
      body: (rowData) => <LastEditedTemplate userContext={userContext} rowData={rowData} isClientFileManager={isClientFileManager} />,
      sortable: true,
    },
    {
      header: 'Shared',
      body: sharedBody,
      exportable: false,
      hidden: isClientFileManager,
      field: 'esign',
    },
    {
      header: 'Actions',
      body: actionsBody,
      exportable: false,
      className: 'fileManager-actions',
      fixed: true,
      width: '200px',
      field: 'action',
    },
  ];
}
